import React from 'react';
import { clsx } from 'clsx';
import { ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight, Minus } from 'lucide-react';
import { REPORT_BASIS_LABELS, type ReportBasis } from '@/types/finance';
import { formatBRL } from '@/utils/money';
import { currentMonth, monthLabel, shiftMonth, type YearMonth } from '@/utils/period';
import { formatPctChange } from '@/utils/reportUtils';

// Pedaços de UI compartilhados por Orçamento e Relatórios (Fase 5).

/** ‹ "novembro de 2026" › com atalho "Mês atual" (sem intervalo livre — relatórios são por mês fechado). */
export const MonthStepper: React.FC<{ value: YearMonth; onChange: (ym: YearMonth) => void; disabled?: boolean; className?: string }> = ({ value, onChange, disabled, className }) => {
  const today = currentMonth();
  const isCurrent = value.year === today.year && value.month === today.month;
  return (
    <div className={clsx('flex items-center gap-1', className)}>
      <button type="button" className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40" onClick={() => onChange(shiftMonth(value, -1))} disabled={disabled} aria-label="Mês anterior" title="Mês anterior">
        <ChevronLeft size={16} />
      </button>
      <span className="flex-1 sm:flex-none min-w-[11rem] text-center inline-block text-sm font-medium text-slate-800 first-letter:uppercase">{monthLabel(value)}</span>
      <button type="button" className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40" onClick={() => onChange(shiftMonth(value, 1))} disabled={disabled} aria-label="Próximo mês" title="Próximo mês">
        <ChevronRight size={16} />
      </button>
      {!isCurrent && (
        <button type="button" className="ml-1 text-xs text-emerald-700 hover:underline whitespace-nowrap" onClick={() => onChange(today)} disabled={disabled}>
          Mês atual
        </button>
      )}
    </div>
  );
};

/** Realizado | Com previstos (segmentado). */
export const BasisToggle: React.FC<{ value: ReportBasis; onChange: (b: ReportBasis) => void; disabled?: boolean; className?: string }> = ({ value, onChange, disabled, className }) => (
  <div className={clsx('inline-flex rounded-lg border border-slate-300 overflow-hidden', className)} role="radiogroup" aria-label="Base do relatório">
    {(['realized', 'projected'] as ReportBasis[]).map((b) => (
      <button
        key={b}
        type="button"
        role="radio"
        aria-checked={value === b}
        disabled={disabled}
        onClick={() => onChange(b)}
        title={b === 'realized' ? 'Só lançamentos com data até hoje' : 'Inclui lançamentos com data futura (previstos)'}
        className={clsx('px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60', value === b ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}
      >
        {REPORT_BASIS_LABELS[b]}
      </button>
    ))}
  </div>
);

/**
 * Variação percentual: em despesa, crescer é ruim (`positiveIsGood = false`, padrão). `null` = sem base
 * de comparação ("—").
 */
export const DeltaBadge: React.FC<{ pct: number | null; positiveIsGood?: boolean; className?: string }> = ({ pct, positiveIsGood = false, className }) => {
  if (pct === null) {
    return (
      <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500', className)} title="Sem base de comparação">
        <Minus size={12} /> —
      </span>
    );
  }
  const up = pct > 0;
  const neutral = pct === 0;
  const good = neutral || up === positiveIsGood;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap', neutral ? 'bg-slate-100 text-slate-600' : good ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700', className)}>
      {neutral ? <Minus size={12} /> : up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {formatPctChange(pct)}
    </span>
  );
};

/** Cartão de indicador: rótulo em caixa alta, valor em R$ e um complemento (texto ou badge). */
export const StatCard: React.FC<{ label: string; cents?: number; tone?: 'neutral' | 'expense' | 'income'; children?: React.ReactNode; className?: string }> = ({ label, cents, tone = 'neutral', children, className }) => (
  <div className={clsx('card p-3', className)}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    {cents !== undefined && (
      <p className={clsx('text-lg font-semibold tabular-nums', tone === 'expense' ? 'text-red-600' : tone === 'income' ? 'text-emerald-700' : cents < 0 ? 'text-red-600' : 'text-slate-800')}>{formatBRL(cents)}</p>
    )}
    {children && <div className="text-small mt-0.5 flex flex-wrap items-center gap-1.5">{children}</div>}
  </div>
);
